<template>
  <div class="app-page">
    <RegisterSplashScreen v-if="showSplash" />
    <Header ref="header">
       <template #header-slot>
        <BreadcrumbNavigation />
      </template>

    </Header>
    <div class="main-page-div">
      <main
        class="main-display-area"
      >
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onBeforeMount, provide,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import Header from '@/components/Header.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { encodeID, decodeID } from '@/assets/js/utils.js';
import { goto_experiments, goto_datasets } from '@/assets/js/links.js';
import RegisterSplashScreen from '@/components/RegisterSplashScreen.vue';

const $toast = useToast();
const store = useStore();
const route = useRoute();
const router = useRouter();
const dataConnect = new DatastoreConnect();

// Show splash only on training/validation session detail pages, not project explorer
const showSplash = computed(() => {
  const name = route.name || '';
  return name.includes('training.details') || name.includes('validation.details') || name.includes('experiment');
});

const isLoading = ref(true);
const publicProjects = ref([]);

provide('isPublic', true);

const experiments = ref(null);
provide('experiments', computed(() => experiments.value));

const currentProject = computed({
  get: () => store.state.projects.currentProject,
  set: (value) => store.commit('projects/setCurrentProject', value),
});

const currentExperiment = computed({
  get: () => store.state.experiments.currentExperiment,
  set: (exp) => store.commit('experiments/setCurrentExperiment', exp),
});

const currentSession = computed({
  get: () => store.state.experiments.currentSession,
  set: (session) => store.commit('experiments/setCurrentSession', session),
});

const routeName = computed(() => route.name);
const moduleLinkText = computed(() => {
  if (routeName.value.includes('datasets')) {
    return 'Explore Experiment Module';
  } else if (routeName.value.includes('experiment')) {
    return 'Explore Datasets Module';
  }
  return '';
});
const moduleLinkRouteName = computed(() => {
  if (routeName.value.includes('datasets')) {
    return 'public.experiment';
  } else if (routeName.value.includes('experiment')) {
    return 'public.datasets';
  }
  return '';
});

onBeforeMount(async () => {
  // Handle expId from route params (e.g., /exp-11ee/trainings)
  if (route.params.expId) {
    const expIdParam = route.params.expId;
    let experimentId = null;
    
    if (expIdParam.startsWith('exp-')) {
      // Decode hex format (exp-11ee) to decimal
      experimentId = parseInt(expIdParam.substring(4), 16);
    } else {
      // Try to parse as numeric
      const parsedId = parseInt(expIdParam);
      experimentId = isNaN(parsedId) ? null : parsedId;
    }
    
    if (experimentId) {
      const experiment = await getExperiment(experimentId);
      if (experiment) {
        currentExperiment.value = experiment;
      }
    }
  } else if (route.query.exp_id) {
    // Fallback for old query param format
    const experiment_id = parseInt(route.query.exp_id);
    const experiment = await getExperiment(experiment_id);
    if (experiment) {
      currentExperiment.value = experiment;
    }
  }
  if (route.query.train_session_id) {
    const train_session_id = parseInt(route.query.train_session_id);
    const trainingSession = await getTrainingSession(train_session_id);
    if (trainingSession) {
      currentSession.value = trainingSession;
    }

    if (currentSession.value && !currentExperiment.value) {
      const experiment = await getExperiment(currentSession.value.trainer_id);
      if (experiment) {
        currentExperiment.value = experiment;
      }
    }
  }
});

onMounted(() => {
  // getProjectList();
});

async function handleModuleLinkClick() {
  if (routeName.value.includes('datasets')) {
    goto_experiments(currentProject.value?.id);
  } else if (routeName.value.includes('experiment')) {
    goto_datasets(currentProject.value?.id);
  }
}

async function handleProjectChanged() {
  goto_experiments(currentProject.value?.id);
  experiments.value = await getExperiments();
}

async function getExperiments() {
  if (store.state.projects.currentProject) {
    const resp = await dataConnect
      .getExperimentList({ project_id: store.state.projects.currentProject.id })
      .catch((error) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve experiments: ${error}`,
          type: 'error',
          position: 'top',
        });
      });

    if (!resp || resp.error || !resp.result) {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiments: ${resp?.error?.message}`,
        type: 'error',
        position: 'top',
      });
      return [];
    }
    return resp.result;
  }
  return [];
}

async function getExperiment(id) {
  return dataConnect
    .getExperiment({ trainer_id: id })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiment: ${error}`,
        type: 'error',
        position: 'top',
      });
    });
}

async function getTrainingSession(training_session_id) {
  return dataConnect
    .getTrainingSession({ trainer_session_id: training_session_id })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }

      const date1 = new Date(data.result.deleted);
      const date2 = new Date('0001-01-01T00:00:00Z');
      if (date1 > date2) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: Session has been removed`,
          type: 'error',
          position: 'top',
        });
        return null;
      }
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve training session: ${error}`,
        type: 'error',
        position: 'top',
      });
    });
}

async function getProjectList() {
  isLoading.value = true;
  publicProjects.value = await dataConnect.getProjectList()
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiment: ${error}`,
        type: "error",
        position: "top",
      });
    });
  let noLT = 0;
  let noDS = 0;
  let noTR = 0;
  let noVA = 0;
  let noAU = 0;

  console.log(publicProjects.value);

  publicProjects.value.forEach((proj) => {
    noAU += proj.no_of_automations;
    noLT += proj.no_of_label_tasks;
    noDS += proj.no_of_datasets;
    noTR += proj.no_of_trainers;
    noVA += proj.no_of_validations;
  });

  for (let k = 0; k < publicProjects.value.length; k++) {
    publicProjects.value[k].total_automations = noAU;
    publicProjects.value[k].total_datasets = noDS;
    publicProjects.value[k].total_trainers = noTR;
    publicProjects.value[k].total_validations = noVA;
    publicProjects.value[k].total_label_tasks = noLT;
  }

  isLoading.value = false;
}
</script>

<style lang="scss" scoped>

.link-button {
  background: var(--color-primary-400);
  width: 300px;
  border-radius: 6px;
  border:var(--color-primary-200) solid 1px;
  margin-left: auto;
  margin-right: 16px;
}

.link-button:hover {
  background:  var(--color-primary-300);
}

</style>
